import { differenceInDays, isBefore, isSameDay, startOfDay } from "date-fns";
import { useEffect, useMemo, useRef } from "react";
import { IInstallationAppointmentForm } from "@features/installation/IInstallationAppointmentForm";
import { useForm, useWatch } from "react-hook-form";
import { useAvailableAppointments } from "@features/installation/hook/useAvailableAppointments";

interface IInitialValues {
    appointmentDate: Date | null;
    timeSlot: string;
}

interface IParams {
    offerId: number;
    initialValues?: IInitialValues;
    onFormValuesChange?(values: IInstallationAppointmentForm | null): void;
}

export const useInstallationAppointment = (props: IParams) => {
    const { offerId, initialValues, onFormValuesChange } = props;
    const { dateList, error, isLoading } = useAvailableAppointments({ offerId });

    // Find closest date to today (prefer future dates, fallback to first available)
    const closestDate = useMemo(() => {
        if (dateList.length === 0) {
            return null;
        }

        const today = startOfDay(new Date());
        const todayOrFutureDates = dateList.filter((date) => !isBefore(date.date, today));

        if (todayOrFutureDates.length > 0) {
            return todayOrFutureDates.reduce((closest, current) => {
                const closestDiff = Math.abs(differenceInDays(closest.date, today));
                const currentDiff = Math.abs(differenceInDays(current.date, today));

                return currentDiff < closestDiff ? current : closest;
            });
        }

        return dateList[0];
    }, [dateList]);

    const formMethods = useForm<IInstallationAppointmentForm>({
        defaultValues: {
            appointmentDate: initialValues?.appointmentDate ?? closestDate?.date ?? null,
            timeSlot: initialValues?.timeSlot ?? closestDate?.slots[0] ?? "",
        },
    });

    const hasInitializedRef = useRef(false);

    // Update form when dates become available and initial values are provided
    useEffect(() => {
        if (
            initialValues?.appointmentDate &&
            initialValues?.timeSlot &&
            dateList.length > 0 &&
            !hasInitializedRef.current
        ) {
            // Find the date in available dates
            const matchingDate = dateList.find((date) => isSameDay(date.date, initialValues.appointmentDate!));

            if (matchingDate) {
                // Check if the initial timeSlot is available for this date
                const isSlotAvailable = matchingDate.slots.includes(initialValues.timeSlot);

                if (isSlotAvailable) {
                    // Set both values using setValue to avoid reset side effects
                    formMethods.setValue("appointmentDate", initialValues.appointmentDate, { shouldValidate: false });
                    formMethods.setValue("timeSlot", initialValues.timeSlot, { shouldValidate: false });
                    hasInitializedRef.current = true;
                } else if (matchingDate.slots.length > 0) {
                    // If slot is not available, use the first available slot
                    formMethods.setValue("appointmentDate", initialValues.appointmentDate, { shouldValidate: false });
                    formMethods.setValue("timeSlot", matchingDate.slots[0], { shouldValidate: false });
                    hasInitializedRef.current = true;
                }
            }
        } else if (
            closestDate &&
            !initialValues?.appointmentDate &&
            !formMethods.getValues("appointmentDate") &&
            !hasInitializedRef.current
        ) {
            // Fallback: use closest date if no initial values
            formMethods.setValue("appointmentDate", closestDate.date, { shouldValidate: false });
            formMethods.setValue("timeSlot", closestDate.slots[0] ?? "", { shouldValidate: false });
            hasInitializedRef.current = true;
        }
    }, [dateList, closestDate, formMethods, initialValues]);

    const watchedValues = useWatch({
        control: formMethods.control,
        defaultValue: formMethods.getValues(),
    });

    useEffect(() => {
        if (onFormValuesChange) {
            if (watchedValues.appointmentDate && watchedValues.timeSlot) {
                onFormValuesChange(watchedValues as IInstallationAppointmentForm);
            } else {
                onFormValuesChange(null);
            }
        }
    }, [watchedValues, onFormValuesChange]);

    return {
        error,
        isLoading,
        dateList,
        formMethods,
    };
};
